import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '~/shared/services/api';

const EXCHANGE_RATES_QUERY_KEY = 'exchange-rates';

const disabledTicketCurrencies = new Set([
    'AED',
    'CUC',
    'MAD',
    'RUB',
    'SVC',
    'TRY',
    'TWD',
    'UAH',
    'XAF',
    'XOF',
]);

export interface CurrencyItem {
    label: string;
    value: string;
}

export interface ConversionResult {
    originalAmount: number;
    convertedAmount: number;
    rate: number;
    fromCurrency: string;
    toCurrency: string;
    via?: string;
}

interface GetExchangeRatesResponse {
    datePeriod: string;
    rates: Record<string, number>;
}

let currencyRatesCache: Record<string, number> = {};

const normalizeCurrencyRates = (rates: Record<string, number> = {}): Record<string, number> =>
    Object.entries(rates).reduce<Record<string, number>>((acc, [pair, value]) => {
        if (!pair) {
            return acc;
        }

        const normalizedPair = pair.toUpperCase();
        const numericValue = Number(value);

        if (Number.isFinite(numericValue)) {
            acc[normalizedPair] = numericValue;
        }

        return acc;
    }, {});

const getCurrencyRates = () => currencyRatesCache;

const hasLoadedCurrencyRates = () => Object.keys(currencyRatesCache).length > 0;

export const setCurrencyRates = (rates: Record<string, number>): Record<string, number> => {
    currencyRatesCache = normalizeCurrencyRates(rates);
    return currencyRatesCache;
};

export const fetchCurrencyRates = async (): Promise<Record<string, number>> => {
    const response = await api.get<GetExchangeRatesResponse>('exchange-rates');
    return setCurrencyRates(response.data?.rates ?? {});
};

export const ensureCurrencyRatesLoaded = async (): Promise<Record<string, number>> => {
    if (hasLoadedCurrencyRates()) {
        return getCurrencyRates();
    }

    return fetchCurrencyRates();
};

const getCurrencyListFromRates = (rates: Record<string, number>): CurrencyItem[] =>
    Object.keys(rates)
        .reduce((acc, pair) => {
            const [fromCurrency, toCurrency] = pair.split('_');

            if (!fromCurrency || !toCurrency) {
                return acc;
            }

            if (!acc.some((item) => item.value === fromCurrency.toLowerCase())) {
                acc.push({
                    label: fromCurrency,
                    value: fromCurrency.toLowerCase(),
                });
            }

            if (!acc.some((item) => item.value === toCurrency.toLowerCase())) {
                acc.push({
                    label: toCurrency,
                    value: toCurrency.toLowerCase(),
                });
            }

            return acc;
        }, [] as CurrencyItem[])
        .filter((item) => !disabledTicketCurrencies.has(item.value.toUpperCase()))
        .sort((a, b) => a.label.localeCompare(b.label));

const convertCurrencyWithRates = (
    _amount: number,
    fromCurrency: string,
    toCurrency: string,
    rates: Record<string, number>,
): ConversionResult => {
    let amount = _amount;

    if (!amount || amount <= 0) {
        amount = 0;
    }

    if (!fromCurrency || !toCurrency) {
        throw new Error('Both currencies must be specified');
    }

    const from = fromCurrency.toUpperCase();
    const to = toCurrency.toUpperCase();

    if (from === to) {
        return {
            originalAmount: amount,
            convertedAmount: amount,
            rate: 1,
            fromCurrency: from,
            toCurrency: to,
        };
    }

    if (!Object.keys(rates).length) {
        throw new Error('Exchange rates are not loaded yet');
    }

    const directKey = `${from}_${to}`;
    if (rates[directKey]) {
        const rate = rates[directKey];
        return {
            originalAmount: amount,
            convertedAmount: parseFloat((amount * rate).toFixed(8)),
            rate,
            fromCurrency: from,
            toCurrency: to,
        };
    }

    const inverseKey = `${to}_${from}`;
    if (rates[inverseKey]) {
        const rate = 1 / rates[inverseKey];
        return {
            originalAmount: amount,
            convertedAmount: parseFloat((amount * rate).toFixed(8)),
            rate,
            fromCurrency: from,
            toCurrency: to,
        };
    }

    const fromToUSD = rates[`${from}_USD`];
    const USDToTo = rates[`USD_${to}`];

    if (fromToUSD && USDToTo) {
        const rate = fromToUSD * USDToTo;
        return {
            originalAmount: amount,
            convertedAmount: parseFloat((amount * rate).toFixed(8)),
            rate,
            fromCurrency: from,
            toCurrency: to,
            via: 'USD',
        };
    }

    const fromToEUR = rates[`${from}_EUR`];
    const EURToTo = rates[`EUR_${to}`];

    if (fromToEUR && EURToTo) {
        const rate = fromToEUR * EURToTo;
        return {
            originalAmount: amount,
            convertedAmount: parseFloat((amount * rate).toFixed(8)),
            rate,
            fromCurrency: from,
            toCurrency: to,
            via: 'EUR',
        };
    }

    throw new Error(`No conversion path found from ${from} to ${to}`);
};

export const useExchangeRatesBootstrap = (enabled = true) =>
    useQuery({
        queryKey: [EXCHANGE_RATES_QUERY_KEY],
        queryFn: ensureCurrencyRatesLoaded,
        enabled,
        staleTime: 1000 * 60 * 30,
    });

export const currencyList = (): CurrencyItem[] => getCurrencyListFromRates(getCurrencyRates());

export const convertCurrency = (
    _amount: number,
    fromCurrency: string,
    toCurrency: string,
): ConversionResult => convertCurrencyWithRates(_amount, fromCurrency, toCurrency, getCurrencyRates());

export const costWithTotalBudgetDolar = (cost: { totalBudget: number; localCurrency: string }) => ({
    ...cost,
    totalBudgetDolar: convertCurrency(Number(cost.totalBudget), cost.localCurrency, 'USD')
        .convertedAmount,
});

export const useCurrencyConverter = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const convert = useCallback(
        async (amount: number, fromCurrency: string, toCurrency: string): Promise<ConversionResult> => {
            setLoading(true);
            setError(null);

            try {
                const rates = await ensureCurrencyRatesLoaded();
                return convertCurrencyWithRates(amount, fromCurrency, toCurrency, rates);
            } catch (err: any) {
                setError(err.message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const convertSync = useCallback((amount: number, fromCurrency: string, toCurrency: string) => {
        try {
            setError(null);
            return convertCurrency(amount, fromCurrency, toCurrency);
        } catch (err: any) {
            setError(err.message);
            throw err;
        }
    }, []);

    return {
        convert,
        convertSync,
        loading,
        error,
    };
};

export const currency = {
    convert: convertCurrency,
    get rates() {
        return getCurrencyRates();
    },
};
